import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { specials } from "@/mocks/specials";

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [specialOpen, setSpecialOpen] = useState(false);

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
      isActive
        ? "text-primary-600 bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Brand */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 cursor-pointer"
            onClick={() => setMobileOpen(false)}
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-film-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg tracking-tight text-foreground-950">
                欧美大片
              </span>
              <span className="font-label text-[10px] tracking-[0.2em] text-foreground-400 uppercase">
                Movie Hub
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" end className={linkClass}>
              <i className="ri-home-5-line" /> 首页
            </NavLink>

            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-apps-2-line" /> 分类
                <i className="ri-arrow-down-s-line" />
              </button>
              {categoryOpen && (
                <div className="absolute top-full left-0 pt-2 w-[420px]">
                  <div className="grid grid-cols-2 gap-1 bg-background-50 border border-background-200 rounded-lg p-2 shadow-sm">
                    {categories.map((cat) => (
                      <Link
                        key={cat.key}
                        to={`/category/${cat.key}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <i className={`${cat.icon} text-accent-600`} />
                        {cat.name}电影
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setSpecialOpen(true)}
              onMouseLeave={() => setSpecialOpen(false)}
            >
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-3-line" /> 专题
                <i className="ri-arrow-down-s-line" />
              </button>
              {specialOpen && (
                <div className="absolute top-full left-0 pt-2 w-[300px]">
                  <div className="flex flex-col gap-1 bg-background-50 border border-background-200 rounded-lg p-2 shadow-sm">
                    {specials.map((sp) => (
                      <Link
                        key={sp.id}
                        to={`/special/${sp.id}`}
                        className="flex flex-col px-3 py-2 rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-800">
                          {sp.title}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {sp.subtitle}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/about" className={linkClass}>
              <i className="ri-information-line" /> 关于我们
            </NavLink>
          </nav>

          {/* Desktop search */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex items-center gap-2 bg-background-100 border border-background-200 rounded-full pl-4 pr-1 py-1 w-56 xl:w-72 focus-within:border-primary-300 transition-colors"
          >
            <i className="ri-search-line text-foreground-400 text-sm" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、影评…"
              className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none py-1"
            />
            <button
              type="submit"
              className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            aria-label="菜单"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 space-y-4">
            <form
              onSubmit={handleSearch}
              className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full pl-4 pr-1 py-2"
            >
              <i className="ri-search-line text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、影评…"
                className="flex-1 bg-transparent text-sm outline-none"
              />
              <button
                type="submit"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </form>

            <nav className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-home-5-line mr-2" />首页
              </Link>
              <div className="px-3 pt-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                电影分类
              </div>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <i className={`${cat.icon} mr-1 text-accent-600`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="px-3 pt-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                影评专题
              </div>
              {specials.map((sp) => (
                <Link
                  key={sp.id}
                  to={`/special/${sp.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className="ri-bookmark-3-line mr-2 text-accent-600" />
                  {sp.title}
                </Link>
              ))}
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-information-line mr-2" />关于我们
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}